import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ChatText, PaperPlaneRight, EnvelopeSimple } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MessageCenterPage() {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [thread, setThread] = useState(null);
  const [threadMsgs, setThreadMsgs] = useState([]);
  const [form, setForm] = useState({ recipient_id: '', subject: '', body: '' });
  const [reply, setReply] = useState('');

  useEffect(() => { fetchMessages(); }, [page]);

  const fetchMessages = async () => {
    try {
      const { data } = await axios.get(`${API}/api/messages?page=${page}`, { withCredentials: true });
      setMessages(data.messages || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load messages'); }
    finally { setLoading(false); }
  };

  const sendMessage = async () => {
    if (!form.recipient_id || !form.body) { toast.error('Recipient and message required'); return; }
    try {
      await axios.post(`${API}/api/messages`, form, { withCredentials: true });
      toast.success('Message sent');
      setComposing(false);
      setForm({ recipient_id: '', subject: '', body: '' });
      fetchMessages();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); }
  };

  const openThread = async (tid) => {
    try {
      const { data } = await axios.get(`${API}/api/messages/thread/${tid}`, { withCredentials: true });
      setThread(tid);
      setThreadMsgs(data.messages || []);
    } catch { toast.error('Failed to load thread'); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    const firstMsg = threadMsgs[0];
    try {
      await axios.post(`${API}/api/messages`, { thread_id: thread, recipient_id: firstMsg?.sender_id || '', body: reply, subject: '' }, { withCredentials: true });
      setReply('');
      openThread(thread);
      fetchMessages();
    } catch { toast.error('Failed to send reply'); }
  };

  const grouped = {};
  messages.forEach(m => { if (!grouped[m.thread_id]) grouped[m.thread_id] = m; });
  const threads = Object.values(grouped);

  return (
    <DashboardLayout>
      <div className="p-6 max-w-5xl" data-testid="message-center-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0A0A]">Message Center</h1>
            <p className="text-sm text-[#6b6b8a] mt-1">{total} messages</p>
          </div>
          <Button data-testid="compose-btn" onClick={() => setComposing(true)} className="bg-[#7d64ff] hover:bg-[#6b4fef]">
            <PaperPlaneRight size={16} className="mr-2" /> Compose
          </Button>
        </div>

        <div className="grid lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2 space-y-2">
            {loading ? <div className="text-center py-12 text-[#9c9db7]">Loading...</div> : threads.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-[#e5e5ea]">
                <EnvelopeSimple size={40} className="mx-auto text-[#9c9db7] mb-3" />
                <p className="text-[#6b6b8a] text-sm">No messages yet</p>
              </div>
            ) : threads.map(m => (
              <button key={m.thread_id} onClick={() => openThread(m.thread_id)} className={`w-full text-left px-4 py-3 bg-white rounded-xl border transition-colors hover:border-[#7d64ff]/40 ${thread === m.thread_id ? 'border-[#7d64ff] bg-[#7d64ff]/5' : 'border-[#e5e5ea]'}`} data-testid={`thread-${m.thread_id}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-[#0A0A0A] truncate">{m.subject || m.sender_name || m.sender_id}</p>
                  {!m.read && <span className="w-2 h-2 rounded-full bg-[#7d64ff]" />}
                </div>
                <p className="text-xs text-[#6b6b8a] truncate mt-0.5">{m.body}</p>
                <p className="text-[10px] text-[#9c9db7] mt-1">{new Date(m.created_at).toLocaleString()}</p>
              </button>
            ))}
          </div>

          <div className="lg:col-span-3 bg-white rounded-xl border border-[#e5e5ea] min-h-[400px] flex flex-col">
            {!thread ? (
              <div className="flex-1 flex items-center justify-center text-[#9c9db7] text-sm">
                <ChatText size={24} className="mr-2" /> Select a conversation
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {threadMsgs.map(m => (
                    <div key={m.message_id} className="max-w-[80%]" data-testid={`msg-${m.message_id}`}>
                      <div className="bg-[#f9f9fb] rounded-lg p-3">
                        <p className="text-xs font-medium text-[#7d64ff] mb-1">{m.sender_name || m.sender_id}</p>
                        <p className="text-sm text-[#0A0A0A]">{m.body}</p>
                      </div>
                      <p className="text-[10px] text-[#9c9db7] mt-1">{new Date(m.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
                <div className="border-t border-[#e5e5ea] p-3 flex gap-2">
                  <Input data-testid="reply-input" value={reply} onChange={e => setReply(e.target.value)} placeholder="Type a reply..." onKeyDown={e => e.key === 'Enter' && sendReply()} />
                  <Button onClick={sendReply} className="bg-[#7d64ff] hover:bg-[#6b4fef]" data-testid="send-reply-btn"><PaperPlaneRight size={16} /></Button>
                </div>
              </>
            )}
          </div>
        </div>

        <Dialog open={composing} onOpenChange={setComposing}>
          <DialogContent className="max-w-md" data-testid="compose-dialog">
            <DialogHeader><DialogTitle>New Message</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Recipient ID</Label><Input data-testid="msg-recipient" value={form.recipient_id} onChange={e => setForm(f => ({ ...f, recipient_id: e.target.value }))} placeholder="user_..." /></div>
              <div><Label>Subject</Label><Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Order inquiry" /></div>
              <div><Label>Message</Label><textarea data-testid="msg-body" value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm min-h-[100px]" placeholder="Type your message..." /></div>
              <Button data-testid="send-msg-btn" onClick={sendMessage} className="w-full bg-[#7d64ff] hover:bg-[#6b4fef]">Send Message</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
